import { IntlShape } from 'react-intl';
import { messages } from '../../i18n';
import { LOCALIZED_PATHS, localizeRoute, delocalizeRoute } from '../constants/url-constants';
import { normalizeFilterKey } from '../facets/normalize-filter-value';
import { toLocationSlug } from '../location/to-location-slug';

export interface IndexUiState {
  [key: string]: any;
  query?: string;
  page?: number;
  sort?: 'relevance' | 'date' | 'salary';
  /** When false, show jobs globally; when true or omitted, show Finland-only. Stored as URL param `finland`. */
  isFinland?: boolean;
  refinementList?: {
    [attribute: string]: string[];
  };
  // Add other UI state properties as needed
}

/**
 * Parses a URL pathname into an IndexUiState object
 * @param pathname The URL pathname to parse
 * @returns An IndexUiState object containing the parsed parameters
 */
// SIMPLE VERSION FOR DEBUGGING
export function parseUrlToIndexUiState(pathname: string): IndexUiState {
  const isServer = typeof window === 'undefined';
  
  // Simple: just parse query parameters, ignore path structure
  let urlObj: URL | null = null;
  try {
    urlObj = new URL(pathname, 'http://localhost');
  } catch {}
  const searchParams = urlObj ? urlObj.searchParams : new URLSearchParams(pathname.split('?')[1] || '');
  
  const state: IndexUiState = {};
  
  // Parse basic query parameters
  const refinementList: Record<string, string[]> = {};
  
  // ADDING BACK: Extract location from path - handle multiple URL patterns like route.ts
  try {
    const rawPath = urlObj ? urlObj.pathname : pathname.split('?')[0] || '/';
    const segs = rawPath.split('/').filter(Boolean);
    
    // Determine if first segment is a locale
    const firstSegment = segs[0]?.toLowerCase();
    const isFirstSegmentLocale = firstSegment && ['en', 'fi', 'sv'].includes(firstSegment);
    
    // Find jobs segment index (accounting for optional locale prefix)
    const jobsIdx = segs.findIndex((s) => s === 'jobs' || s === 'tyopaikat' || s === 'lediga-jobb' || s === 'jobb');
    
    if (jobsIdx !== -1) {
      // Check if there's a location segment after jobs
      const locationSegment = segs[jobsIdx + 1];
      if (
        locationSegment &&
        (locationSegment === 'location' ||
          locationSegment === 'alue' ||
          locationSegment === 'omrade' ||
          locationSegment === 'område' ||
          locationSegment === 'omr%C3%A5de')
      ) {
        const locSeg = segs[jobsIdx + 2];
        if (locSeg) {
          // Support multiple locations separated by "+"
          const tokens = locSeg.split('+');
          const decodedList = tokens
            .map((t) => {
              const decoded = decodeURIComponent(t);
              // Handle different location formats:
              // 1. "Remote_-_Europe" -> "Remote - Europe" (replace _- with -)
              // 2. "Helsinki_Finland" -> "Helsinki, Finland" (replace _ with , )
              // 3. "United_States" -> "United States" (single country, replace _ with space)
              if (decoded.includes('_-_')) {
                return decoded.replace(/_/g, ' ').trim();
              } else if (decoded.includes('_')) {
                // Check if it's a single country name (like "United_States", "United_Kingdom")
                // vs a city-country pair (like "Helsinki_Finland", "New_York_United_States")
                const parts = decoded.split('_');
                if (parts.length === 2) {
                  const [first, second] = parts;
                  // If both parts are single words and the second is a common country suffix,
                  // treat as single country name
                  const countrySuffixes = ['States', 'Kingdom', 'Arabia', 'Emirates', 'Republic', 'Federation'];
                  if (countrySuffixes.some(suffix => second.includes(suffix))) {
                    return decoded.replace(/_/g, ' ').trim();
                  }
                } else if (parts.length >= 3) {
                  // Multi-part location like "New_York_United_States" or "San_Francisco_United_States"
                  // Check if the last 1-2 parts form a country name
                  const countrySuffixes = ['States', 'Kingdom', 'Arabia', 'Emirates', 'Republic', 'Federation'];
                  
                  // Check if last part is a country suffix
                  const lastPart = parts[parts.length - 1];
                  if (countrySuffixes.some(suffix => lastPart.includes(suffix))) {
                    // Check if it's a two-word country (like "United States")
                    if (lastPart === 'States' && parts[parts.length - 2] === 'United') {
                      // It's "United States" - take last 2 parts as country
                      const cityParts = parts.slice(0, -2);
                      const country = 'United States';
                      return `${cityParts.join(' ')}, ${country}`.trim();
                    } else {
                      // Single-word country suffix
                      const cityParts = parts.slice(0, -1);
                      const country = lastPart;
                      return `${cityParts.join(' ')}, ${country}`.trim();
                    }
                  }
                }
                // Default to city, country format
                return decoded.replace(/_/g, ', ').trim();
              }
              return decoded.trim();
            })
            .filter(Boolean);
          if (decodedList.length > 0) {
            refinementList.location = decodedList;
          }
        }
      }
    }
  } catch (error) {
    // Silently handle path parsing errors
  }
  
  // List of tracking/marketing parameters to ignore (not treated as search filters)
  const ignoredParams = new Set([
    // UTM parameters (Google Ads, marketing campaigns)
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    // Google Click ID
    'gclid',
    // Google Browser ID (used by Google crawlers)
    'gbraid',
    'wbraid',
    // Facebook Click ID
    'fbclid',
    // Microsoft Click ID
    'msclkid',
    // Other common tracking parameters
    'ref',
    'source',
    'medium',
    'campaign',
    'term',
    'content',
    // Google Analytics
    '_ga',
    '_gid',
    // Other analytics/tracking
    'mc_cid',
    'mc_eid',
    'utm_id',
    'utm_source_platform',
    'utm_creative_format',
    'utm_marketing_tactic',
  ]);

  for (const [key, value] of searchParams.entries()) {
    // Normalize parameter key to lowercase for case-insensitive matching
    const normalizedKey = normalizeFilterKey(key);
    
    // Skip ignored tracking parameters
    if (ignoredParams.has(normalizedKey)) {
      continue;
    }
    
    if (normalizedKey === 'query' || normalizedKey === 'search') {
      state.query = value;
    } else if (normalizedKey === 'page') {
      const pageNum = parseInt(value, 10);
      if (!isNaN(pageNum)) {
        state.page = pageNum;
      }
    } else if (normalizedKey === 'sort') {
      if (value === 'relevance' || value === 'date' || value === 'salary') {
        state.sort = value;
      }
    } else if (normalizedKey === 'finland' || normalizedKey === 'isfinland') {
      state.isFinland = value === '1' || value === 'true';
    } else if (key.includes('[sort]') || key.endsWith('[sort]')) {
      // Skip old format sort parameters like "en_search[sort]"
      // These are legacy parameters that should be ignored
      continue;
    } else {
      // Treat other parameters as filters
      // Use the original key so it matches Algolia facet attribute names (e.g., jobType)
      const filterKey = key;
      if (!refinementList[filterKey]) {
        refinementList[filterKey] = [];
      }
      refinementList[filterKey].push(value);
    }
  }
  
  // Add refinementList if we have any filters
  if (Object.keys(refinementList).length > 0) {
    state.refinementList = refinementList;
  }
  
  return state;
}

// SIMPLE VERSION FOR DEBUGGING
export function generateUrlFromIndexUiState(indexUiState: IndexUiState, indexName: string, originalPath?: string): string {
  const isServer = typeof window === 'undefined';
  
  // ADDING BACK: Complex URL generation logic
  const params = new URLSearchParams();
  // Infer locale from index name like "en_search" | "fi_hae" | "sv_sok"
  const localeMatch = indexName.match(/^([a-z]{2})[_-]/i);
  const locale = (localeMatch ? localeMatch[1] : 'en').toLowerCase();
  
  // Get localized job segment
  const jobSegments = { en: 'jobs', fi: 'tyopaikat', sv: 'lediga-jobb' };
  const jobSegment = jobSegments[locale as keyof typeof jobSegments] || 'jobs';
  // Get localized location segment
  const locationSegments = { en: 'location', fi: 'alue', sv: 'omrade' } as const;
  const locationSegment = (locationSegments as any)[locale] || 'location';
  
  // Check if we have any search parameters (query, filters, sort, page > 1)
  const hasQuery = indexUiState.query && indexUiState.query.trim().length > 0;
  const hasFilters = indexUiState.refinementList && Object.keys(indexUiState.refinementList).length > 0;
  const hasSort = indexUiState.sort && indexUiState.sort !== 'relevance';
  const hasPage = indexUiState.page && indexUiState.page > 1;
  
  // Only add /jobs segment if we have search parameters AND the original path doesn't already have a job segment
  // If no search parameters and we have an original path, preserve it
  let path = '';
  if (hasQuery || hasFilters || hasSort || hasPage) {
    // Check if originalPath already has a job segment
    const hasJobSegment =
      originalPath &&
      (originalPath.includes('/jobs') ||
        originalPath.includes('/tyopaikat') ||
        originalPath.includes('/lediga-jobb') ||
        originalPath.includes('/jobb'));
    
    if (hasJobSegment) {
      // Preserve the original path structure if it already has a job segment
      path = originalPath;
    } else {
      // Add the job segment if the original path doesn't have one
      path = `/${locale}/${jobSegment}`;
    }
  } else if (originalPath) {
    // Preserve the original path when there are no search parameters
    path = originalPath;
  } else {
    // Fallback to default behavior if no original path provided
    path = locale === 'en' ? '/' : `/${locale}`;
  }

  // Location in path (support multiple, joined by "+")
  const locValues = indexUiState.refinementList?.location || [];
  
  // Check if the path already has a location segment
  const hasLocationInPath = originalPath && (
    originalPath.includes('/location/') ||
    originalPath.includes('/alue/') ||
    originalPath.includes('/omrade/') ||
    originalPath.includes('/område/') ||
    originalPath.includes('/omr%C3%A5de/')
  );
  
  if (locValues && locValues.length > 0) {
    // We have location values - add or update location segment
    const encodedLocs = locValues
      .map((v) => toLocationSlug(String(v)))
      .filter(Boolean)
      .join('+');
    
    if (encodedLocs) {
      if (!hasLocationInPath) {
        // Add location segment if it's not already in the path
        path += `/${locationSegment}/${encodedLocs}`;
      } else {
        // Replace existing location segment with updated locations
        const locationSegments = { en: 'location', fi: 'alue', sv: 'omrade' };
        const currentLocationSegment = (locationSegments as any)[locale] || 'location';
        
        // Remove existing location segment and add updated one
        // Handle both encoded and unencoded versions
        const encodedLocationSegment =
          currentLocationSegment === 'omrade' ? 'omrade' : currentLocationSegment;
        const legacyEncodedSegment =
          currentLocationSegment === 'omrade' ? 'omr%C3%A5de' : currentLocationSegment;
        const pathWithoutLocation = path.replace(new RegExp(`/(${currentLocationSegment}|${encodedLocationSegment})/[^/?]+`), '');
        const legacyCleaned = pathWithoutLocation.replace(
          new RegExp(`/(${legacyEncodedSegment})/[^/?]+`),
          ''
        );
        path = legacyCleaned + `/${currentLocationSegment}/${encodedLocs}`;
      }
    }
  } else if (hasLocationInPath) {
    // No location values but path has location segment - remove it
    const locationSegments = { en: 'location', fi: 'alue', sv: 'omrade' };
    const currentLocationSegment = (locationSegments as any)[locale] || 'location';
    
    // Remove existing location segment
    // Handle both encoded and unencoded versions
    const encodedLocationSegment =
      currentLocationSegment === 'omrade' ? 'omrade' : currentLocationSegment;
    const legacyEncodedSegment =
      currentLocationSegment === 'omrade' ? 'omr%C3%A5de' : currentLocationSegment;
    path = path
      .replace(new RegExp(`/(${currentLocationSegment}|${encodedLocationSegment})/[^/?]+`), '')
      .replace(new RegExp(`/(?:${legacyEncodedSegment})/[^/?]+`), '');
  }

  // Add query if present
  if (indexUiState.query) params.set('search', indexUiState.query);

  // Add refinementList parameters
  if (indexUiState.refinementList) {
    for (const [attribute, values] of Object.entries(indexUiState.refinementList)) {
      if (attribute === 'location') continue; // already encoded in path
      if (attribute === 'sort' || attribute.includes('[sort]')) {
        // Skip sort from refinementList - it's handled separately
        continue;
      }
      const list = Array.isArray(values) ? values : [values as any];
      // Filter out empty/null/undefined values but preserve valid ones (including 0 and negative numbers for timestamps)
      const validValues = list.filter((v) => v !== undefined && v !== null && String(v).trim() !== '');
      if (validValues.length === 0) {
        continue;
      }
      validValues.forEach((value) => {
        // Use lowercase values in URLs for case-insensitive matching
        params.append(attribute, String(value).toLowerCase());
      });
    }
  }

  // Add sort parameter (omit if relevance to keep URL clean)
  if (indexUiState.sort && indexUiState.sort !== 'relevance') {
    params.set('sort', indexUiState.sort);
  }

  // Add page parameter only if page > 0 (0-based: page 0 = first page, page 1 = second page, etc.)
  if (indexUiState.page !== undefined && indexUiState.page !== null && indexUiState.page > 0) {
    params.set('page', indexUiState.page.toString());
  }

  // Finland filter: only put in URL when false so default (true) stays out of the query string
  if (indexUiState.isFinland === false) {
    params.set('finland', '0');
  }

  const queryString = params.toString();
  const result = queryString ? `${path}?${queryString}` : path;
  
  return result;
}

/**
 * Maps a path to its corresponding route key
 * @param path The path to map (without language prefix)
 * @returns The corresponding route key or null if not found
 */
function mapPathToRouteKey(path: string): string | null {
  // Handle dynamic routes
  for (const lang of ['en', 'fi', 'sv'] as const) {
    if (path.startsWith(`/${LOCALIZED_PATHS[lang].job}/`)) {
      return 'route.jobDetailPage';
    }
  }

  // Try to find a matching route key in any language
  for (const lang of ['en', 'fi', 'sv'] as const) {
    const langMessages = messages[lang];
    const routeKey = Object.entries(langMessages).find(([key, value]) => key.startsWith('route.') && value === path)?.[0];

    if (routeKey) {
      return routeKey;
    }
  }

  return null;
}

/**
 * Checks if the current pathname represents a search results page (SRP)
 * @param pathname The URL pathname
 * @param searchParams The URL search parameters
 * @returns true if this is an SRP, false otherwise
 */
function isSearchResultsPage(pathname: string, searchParams: URLSearchParams): boolean {
  // Check for search parameters that indicate an active search
  const hasSearchParams = 
    searchParams.has('search') ||
    searchParams.has('query') ||
    searchParams.has('en_search[query]') ||
    searchParams.has('fi_hae[query]') ||
    searchParams.has('sv_sok[query]') ||
    searchParams.has('sort') ||
    searchParams.has('page') ||
    searchParams.has('jobType') ||
    searchParams.has('remoteness') ||
    searchParams.has('location') ||
    searchParams.has('salaryRangeMin') ||
    searchParams.has('vacationWeeks') ||
    searchParams.has('companyHeadcountRangeMax') ||
    Array.from(searchParams.keys()).some(key => key.includes('[refinementList]'));

  // Check if pathname is home page or search page
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0]?.toLowerCase();
  const isLocale = firstSegment && ['en', 'fi', 'sv'].includes(firstSegment);
  
  // Home page (root or locale-only)
  if (segments.length === 0 || (segments.length === 1 && isLocale)) {
    return true;
  }

  // Check for jobs listing paths (plural, not singular job detail pages)
  // Job detail uses singular: job/tyopaikka/jobb
  // Jobs listing uses plural: jobs/tyopaikat/lediga-jobb
  const jobsListingSegments = ['jobs', 'tyopaikat', 'lediga-jobb'];
  const jobsIndex = isLocale 
    ? segments.findIndex((s, idx) => idx > 0 && jobsListingSegments.includes(s))
    : segments.findIndex(s => jobsListingSegments.includes(s));
  
  if (jobsIndex !== -1) {
    // This is a jobs listing page (SRP)
    return true;
  }
  
  // Check if this is a job detail page (singular job path)
  // Job detail pattern: /{locale}/{job}/{jobId}/{companyId}/{seoSlug}
  const jobDetailPattern = /^\/(en|fi|sv)\/(job|tyopaikka|jobb)\/([^/]+)\/([^/]+)/;
  if (jobDetailPattern.test(pathname)) {
    return false; // Job detail page, not SRP
  }

  return hasSearchParams;
}

/**
 * Converts a URL pathname to the same route in a target language.
 * Accepts either a pathname (e.g. /sv/jobs) or a full URL (e.g. http://localhost:3000/sv/jobs); full URLs are normalized to pathname + search.
 *
 * @param pathnameOrUrl The current URL pathname, or full URL (pathname may include search parameters)
 * @param targetLanguage The target language code (e.g., 'en', 'fi', 'sv')
 * @param intl The IntlShape object from react-intl
 * @returns The same page in the target language (path only), or home page for SRPs
 */
export function convertUrlToLanguage(pathnameOrUrl: string, targetLanguage: string, intl: IntlShape): string {
  let pathname = pathnameOrUrl;
  if (pathnameOrUrl.includes('://')) {
    try {
      const url = new URL(pathnameOrUrl);
      pathname = url.pathname + (url.search ? url.search : '');
    } catch {
      pathname = pathnameOrUrl;
    }
  }

  // Split pathname and search parameters
  const [cleanPathname, searchParamsString] = pathname.split('?');
  const searchParams = new URLSearchParams(searchParamsString || '');
  
  // Check if this is a search results page (SRP)
  // For SRPs, we can't preserve facet values across languages, so go to home page
  if (isSearchResultsPage(cleanPathname, searchParams)) {
    return `/${targetLanguage}`;
  }
  
  // Extract current locale and path without locale prefix
  const segments = cleanPathname.split("/").filter(Boolean);
  let currentLocale: 'en' | 'fi' | 'sv' = 'en';
  let pathWithoutLocale = cleanPathname;
  
  // Check if first segment is a locale
  const firstSegment = segments[0]?.toLowerCase();
  if (firstSegment && ['en', 'fi', 'sv'].includes(firstSegment)) {
    currentLocale = firstSegment as 'en' | 'fi' | 'sv';
    pathWithoutLocale = '/' + segments.slice(1).join('/');
    if (pathWithoutLocale === '/') {
      // Handle language home routes (e.g., "/en" -> "/fi")
      return `/${targetLanguage}`;
    }
  }
  
  // Handle job detail pages specially
  // Pattern: /{job}/{jobId}/{companyId}/{seoSlug}
  const jobSlugs = Object.values(LOCALIZED_PATHS).map((l) => l.job);
  const jobDetailPattern = new RegExp(`^/(${jobSlugs.map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})/([^/]+)/([^/]+)(?:/(.+))?$`);
  const jobMatch = pathWithoutLocale.match(jobDetailPattern);
  
  if (jobMatch) {
    // Extract jobId, companyId, and seoSlug
    const [, , jobId, companyId, seoSlug] = jobMatch;
    const targetJobSlug = LOCALIZED_PATHS[targetLanguage as keyof typeof LOCALIZED_PATHS].job;
    
    // Preserve jobId and companyId, keep seoSlug as-is (it will be regenerated by the page if needed)
    const newPath = seoSlug 
      ? `/${targetLanguage}/${targetJobSlug}/${jobId}/${companyId}/${seoSlug}`
      : `/${targetLanguage}/${targetJobSlug}/${jobId}/${companyId}`;
    
    return searchParamsString ? `${newPath}?${searchParamsString}` : newPath;
  }
  
  // For static pages, use the route localization system
  // Convert localized path back to English equivalent
  const englishPath = delocalizeRoute(pathWithoutLocale, currentLocale);
  
  // Convert English path to target language
  const localizedPath = localizeRoute(englishPath, targetLanguage as 'en' | 'fi' | 'sv');
  
  // Add target language prefix (including for English)
  const newPath = `/${targetLanguage}${localizedPath}`;
  
  // Preserve any search parameters for static pages
  return searchParamsString ? `${newPath}?${searchParamsString}` : newPath;
}